export interface PersonTypeTranslationItem {
  language: string
  name: string
  description?: string
}

export interface PersonType {
  _id?: string
  name?: string
  code: string
  description?: string
  defaultPriceMultiplier: number
  minAge: number
  maxAge: number
  isActive: boolean
  translations?: PersonTypeTranslationItem[]
}

const DEFAULT_LANGUAGES = [
  { language: 'en', name: '', description: '' },
  { language: 'fr', name: '', description: '' },
  { language: 'de', name: '', description: '' },
]

export const initialPersonType: PersonType = {
  name: '',
  code: '',
  description: '',
  defaultPriceMultiplier: 1,
  minAge: 0,
  maxAge: 999,
  isActive: true,
  translations: [...DEFAULT_LANGUAGES]
}

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

/** Langue UI du backoffice (ex. fr) — alignée sur StickyLanguageSelector / i18nConfig. */
export function getBackofficeDefaultLanguage(): string {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(I18N_CONFIG_KEY) : null
    const c = raw ? JSON.parse(raw) : {}
    const code = String(c.selectedLang ?? 'fr').trim().toLowerCase()
    return code.slice(0, 5) || 'fr'
  } catch {
    return 'fr'
  }
}

/** Remplit les langues vides avec le texte de la langue par défaut (ou la première saisie). */
export function applyDefaultLanguageToPersonTypeTranslations(
  translations: PersonTypeTranslationItem[],
  defaultLang: string,
): PersonTypeTranslationItem[] {
  const dl = defaultLang.trim().toLowerCase()
  const fromDefault = translations.find(
    (t) => String(t.language).toLowerCase() === dl && t.name?.trim(),
  )
  const firstAny = translations.find((t) => t.name?.trim())
  const baseName = fromDefault?.name?.trim() || firstAny?.name?.trim() || ''
  const baseDesc = fromDefault?.description?.trim() || firstAny?.description?.trim() || ''
  return translations.map((t) => ({
    ...t,
    name: t.name?.trim() || baseName,
    description: (t.description ?? '').trim() || baseDesc,
  }))
}

/** Avant POST/PATCH : appliquer la langue par défaut aux traductions vides + racine `name`. */
export function finalizePersonTypeForSave(values: PersonType): PersonType {
  const dl = getBackofficeDefaultLanguage()
  const translations = applyDefaultLanguageToPersonTypeTranslations(values.translations || [], dl)
  const lc = dl.toLowerCase()
  const fromDl = translations.find((t) => String(t.language).toLowerCase() === lc && t.name?.trim())
  const name =
    (fromDl?.name?.trim() ??
      translations.find((t) => t.name?.trim())?.name?.trim() ??
      values.code ??
      '').trim()
  return { ...values, translations, name }
}
